import { useCallback, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { color, EL_COLORS } from '@/colors';
import { Flex, FlexCol } from '@/UI/styles';
import { useCaptureElementWheelScroll } from '@/hooks/useCaptureElementWheelScroll';
import { ThreeDProps } from '../Common/ImageList/types';
import { GridItem } from './GridItem';
import type { NodeId, FileKind } from 'web';
import type { IndexedUploadedAsset } from '../Common/ImageList/ImageList';

const NODE_WIDTH = 422;

type IndextFileKind = FileKind & { globalIndex: number };
type IndexedFile = IndexedUploadedAsset | IndextFileKind;

export interface GridViewerProps {
  nodeId: NodeId;
  files: IndexedFile[];
  selectedIndex: number;
  onFileSelect: (index: number) => void;
  className?: string;
  shouldUseLegacyFileViewer?: boolean;
  // props for legacy GalleryFileViewer
  threeDProps?: ThreeDProps;
  maxSteps?: number;
  onContextMenu?: (event: React.MouseEvent, index: number) => void;
  onTextChange?: (index: number, newValue: string) => void;
  // eslint-disable-next-line react19-ref/require-ref-object-null
  videoRefs?: React.RefObject<HTMLVideoElement>[];
}

export const GridViewer = ({
  nodeId,
  files,
  selectedIndex,
  onFileSelect,
  className,
  shouldUseLegacyFileViewer = false,
  // props for legacy GalleryFileViewer
  threeDProps,
  maxSteps,
  onContextMenu,
  onTextChange,
  videoRefs,
}: GridViewerProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isSingleFile = files.length === 1;

  const distributeFilesIntoCloumns = useCallback(() => {
    const fileCount = files.length;
    const columns = fileCount === 1 ? 1 : fileCount <= 4 ? 2 : 3;
    const cols: Array<{ file: IndexedFile }[]> = Array.from({ length: columns }, () => []);

    files.forEach((file, index) => {
      const columnIndex = index % columns;
      cols[columnIndex].push({ file });
    });

    return cols;
  }, [files]);

  const getItemSquareSize = useCallback((columnCount, hasDynamicItemHeight) => {
    const columnWidth = NODE_WIDTH / columnCount;
    return !hasDynamicItemHeight ? columnWidth : undefined;
  }, []);

  const hasDynamicItemHeight = files && (files[0]?.type === 'image' || files[0]?.type === 'video');
  const gridColumns = useMemo(distributeFilesIntoCloumns, [distributeFilesIntoCloumns]);

  const fixedItemSquareSize = useMemo(
    () => getItemSquareSize(gridColumns.length, hasDynamicItemHeight),
    [gridColumns.length, hasDynamicItemHeight, getItemSquareSize],
  );

  // Handle wheel events when node is focused
  useCaptureElementWheelScroll(scrollContainerRef);

  return (
    <FlexCol className={className} sx={{ width: '100%' }}>
      <Box
        ref={scrollContainerRef}
        sx={{
          height: 'auto',
          maxHeight: isSingleFile ? 'none' : '432px',
          overflow: 'auto',
          border: `1px solid ${EL_COLORS.BoxBorder}`,
          p: 0.25,
          borderRadius: 2,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: color.Black88,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: color.Black40,
            '&:hover': {
              backgroundColor: color.Black64,
            },
          },
        }}
      >
        <Flex sx={{ gap: 0.5 }}>
          {gridColumns.map((columnFiles, columnIndex) => (
            <FlexCol key={`column-${columnIndex}`} sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
              {columnFiles.map(({ file }) => (
                <GridItem
                  shouldUseLegacyFileViewer={shouldUseLegacyFileViewer}
                  key={`file-${file.globalIndex}`}
                  nodeId={nodeId}
                  file={file}
                  index={file.globalIndex}
                  isSelected={file.globalIndex === selectedIndex}
                  onFileClick={onFileSelect}
                  selected={selectedIndex}
                  // props for legacy GalleryFileViewer
                  threeDProps={threeDProps}
                  maxSteps={maxSteps}
                  onContextMenu={onContextMenu}
                  onTextChange={onTextChange}
                  videoRef={videoRefs?.[file.globalIndex]}
                  fixedItemSize={fixedItemSquareSize}
                />
              ))}
            </FlexCol>
          ))}
        </Flex>
      </Box>
    </FlexCol>
  );
};
